/* eslint-disable sonarjs/cognitive-complexity*/
import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";
import { useAuthContext } from "providers/Auth";
import { formatToCurrency } from "shared/utils";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import moment from "moment";
import { useState } from "react";
import { RentalInfoForm } from "shared/app/RentalInfoForm";
import { useFormTyped, type Register } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import addressApi from "api/landlord/address";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  PMSRentalInfoForm,
  type HandleChange
} from "shared/app/RentalInfoForm/PMSRentalInfoForm";
import * as yup from "yup";

// Prefix, not a parse: the stored value is a UTC timestamp and the picker emits a bare
// YYYY-MM-DD, so parsing the latter reads it as local midnight and rolls the day back
// east of UTC. This is how DateInput itself derives the day it displays.
const asCalendarDay = (value?: string) => (value ? value.slice(0, 10) : "");

const getRentalInfoValidationSchema = ({
  homeSiteRentEnabled
}: {
  homeSiteRentEnabled: boolean;
}) => {
  return yup.object().shape({
    monthly_rent_amount: homeSiteRentEnabled
      ? yup.number().optional()
      : yup
          .number()
          .required("Monthly rent is required")
          .typeError("Monthly rent must be a valid number")
          .min(1, "Monthly rent must be at least $1"),

    home_rent_amount: homeSiteRentEnabled
      ? yup
          .number()
          .required("Home rent is required")
          .typeError("Home rent must be a valid number")
          .min(0, "Home rent must be at least $0")
      : yup.number().optional(),
    site_rent_amount: homeSiteRentEnabled
      ? yup
          .number()
          .required("Site rent is required")
          .typeError("Site rent must be a valid number")
          .min(0, "Site rent must be at least $0")
      : yup.number().optional(),

    available_at: yup
      .string()
      .nullable()
      .test("is-valid-date", "Please enter a valid date", (value) => {
        // Allow null values
        if (!value) return true;
        const date = moment(value);
        return date.isValid();
      }),

    ready_for_showing_at: yup
      .string()
      .nullable()
      .test("is-valid-date", "Please enter a valid date", (value) => {
        if (!value) return true;
        const date = moment(value);
        return date.isValid();
      }),

    application_fee_cents: yup
      .number()
      .nullable()
      .min(0, "Application fee must be at least $0")
      .optional(),
    security_deposit_cents: yup
      .number()
      .nullable()
      .min(0, "Security deposit must be at least $0")
      .optional()
  });
};

const EditRentalInfoForm = ({
  onClose,
  unitId,
  onSuccess,
  initialValues,
  pmsId
}: {
  onClose: () => void;
  unitId: number;
  onSuccess: () => void;
  initialValues: {
    monthly_rent_amount?: number;
    home_rent_amount?: number;
    site_rent_amount?: number;
    occupied: boolean;
    available_at?: string;
    ready_for_showing_at?: string;
    waitlist?: boolean;
    pms_monthly_rent_amount_override?: boolean;
    pms_home_rent_amount_override?: boolean;
    pms_site_rent_amount_override?: boolean;
    pms_occupied_override?: boolean;
    pms_available_at_override?: boolean;
    pms_ready_for_showing_at_override?: boolean;

    pms_monthly_rent_amount?: number;
    pms_home_rent_amount?: number;
    pms_site_rent_amount?: number;
    pms_occupied?: boolean;
    pms_available_at?: string;
    pms_ready_for_showing_at?: string;
    sale_price?: number;
    purchase_price?: number;
    application_fee_cents?: number | null;
    security_deposit_cents?: number | null;
    application_fee_overridden?: boolean;
    security_deposit_overridden?: boolean;
    pms_values?: Record<string, unknown>;
    pms_overrides?: Record<string, boolean>;
  };
  pmsId?: string;
}) => {
  const { type, permissions, pms } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const securityDepositSynced = pms.isAppfolio || pms.isRentManager;

  const { register, onSubmit, values, handleChange } = useFormTyped(
    initialValues,
    getRentalInfoValidationSchema({ homeSiteRentEnabled })
  );

  const buildRentFields = () => {
    if (
      homeSiteRentEnabled &&
      values.home_rent_amount !== undefined &&
      values.site_rent_amount !== undefined
    ) {
      const homeCents = Math.round(
        parseFloat(String(values.home_rent_amount)) * 100
      );
      const siteCents = Math.round(
        parseFloat(String(values.site_rent_amount)) * 100
      );
      return {
        monthly_rent_amount: {
          cents: homeCents + siteCents,
          currency: "USD"
        },
        home_rent_amount: { cents: homeCents, currency: "USD" },
        site_rent_amount: { cents: siteCents, currency: "USD" }
      };
    }

    return {
      monthly_rent_amount:
        values.monthly_rent_amount && values.monthly_rent_amount > 0
          ? {
              cents: Math.round(values.monthly_rent_amount * 100),
              currency: "USD"
            }
          : undefined
    };
  };

  const salePriceSynced =
    Boolean(pmsId) && values.pms_overrides?.sale_price !== false;
  const salePriceChanged = values.sale_price !== initialValues.sale_price;

  // When sync is on, push the PMS value to the backend so the column matches
  // what the user sees. Previously we skipped sale_price entirely, which left
  // the stale manually-edited value in the DB until the next scheduled PMS
  // sync overwrote it. When PMS has no sale price either, clear to $0 so the
  // DB column doesn't keep a stale manual value.
  const buildSalePricePayload = () => {
    if (salePriceSynced) {
      const pmsSalePrice = (
        values.pms_values as
          | { sale_price?: { cents: number; currency: string } }
          | undefined
      )?.sale_price;
      return pmsSalePrice
        ? { cents: pmsSalePrice.cents, currency: pmsSalePrice.currency }
        : { cents: 0, currency: "USD" };
    }
    return salePriceChanged
      ? { cents: Math.round((values.sale_price || 0) * 100), currency: "USD" }
      : undefined;
  };

  const purchasePriceSynced =
    Boolean(pmsId) && values.pms_overrides?.purchase_price !== false;
  const purchasePriceChanged =
    values.purchase_price !== initialValues.purchase_price;

  const buildPurchasePricePayload = () => {
    if (purchasePriceSynced) {
      const pmsPurchasePrice = (
        values.pms_values as
          | { purchase_price?: { cents: number; currency: string } }
          | undefined
      )?.purchase_price;
      return pmsPurchasePrice
        ? { cents: pmsPurchasePrice.cents, currency: pmsPurchasePrice.currency }
        : { cents: 0, currency: "USD" };
    }
    return purchasePriceChanged
      ? {
          cents: Math.round((values.purchase_price || 0) * 100),
          currency: "USD"
        }
      : undefined;
  };

  // PMS fee overrides. Send a field only when the partner actually changed it so
  // a routine save doesn't flip the server-side override flag and permanently
  // detach the value from PMS syncs.
  const applicationFeeChanged =
    values.application_fee_cents !== initialValues.application_fee_cents;
  const securityDepositChanged =
    values.security_deposit_cents !== initialValues.security_deposit_cents;

  const applicationFeeOverriddenChanged =
    values.application_fee_overridden !==
    initialValues.application_fee_overridden;
  const securityDepositOverriddenChanged =
    values.security_deposit_overridden !==
    initialValues.security_deposit_overridden;

  // An empty available_at is now the API's explicit "erase the date" signal, so a
  // save that never touched the field must not carry one. Compared as calendar
  // days: the column is a timestamp, so the stored value has a time part the
  // picker's YYYY-MM-DD output never has.
  const availableAtChanged =
    asCalendarDay(values.available_at) !==
    asCalendarDay(initialValues.available_at);
  const readyForShowingAtChanged =
    asCalendarDay(values.ready_for_showing_at) !==
    asCalendarDay(initialValues.ready_for_showing_at);

  const buildPmsFeePayload = () => ({
    ...(pms.isAppfolio &&
    values.application_fee_overridden &&
    (applicationFeeChanged || applicationFeeOverriddenChanged)
      ? { application_fee_cents: values.application_fee_cents ?? null }
      : {}),
    ...(pms.isAppfolio && applicationFeeOverriddenChanged
      ? { application_fee_overridden: values.application_fee_overridden }
      : {}),
    ...(securityDepositSynced &&
    values.security_deposit_overridden &&
    (securityDepositChanged || securityDepositOverriddenChanged)
      ? { security_deposit_cents: values.security_deposit_cents ?? null }
      : {}),
    ...(securityDepositSynced && securityDepositOverriddenChanged
      ? { security_deposit_overridden: values.security_deposit_overridden }
      : {})
  });

  const { mutate: updateProperty, isPending } = useMutation({
    mutationFn: () => {
      return addressApi.editUnit(unitId, {
        ...buildRentFields(),
        sale_price: buildSalePricePayload(),
        purchase_price: buildPurchasePricePayload(),
        ...buildPmsFeePayload(),
        occupied: values.occupied,
        // ?? "" so an erase survives JSON.stringify, which drops an undefined value
        // and would turn the erase into "leave it alone".
        ...(availableAtChanged
          ? { available_at: values.available_at ?? "" }
          : {}),
        ...(readyForShowingAtChanged
          ? { ready_for_showing_at: values.ready_for_showing_at ?? "" }
          : {}),
        waitlist: Boolean(values.occupied) && Boolean(values.waitlist),
        pms_monthly_rent_amount_override:
          values.pms_monthly_rent_amount_override,
        pms_home_rent_amount_override: values.pms_home_rent_amount_override,
        pms_site_rent_amount_override: values.pms_site_rent_amount_override,
        pms_occupied_override: values.pms_occupied_override,
        pms_available_at_override: values.pms_available_at_override,
        pms_ready_for_showing_at_override:
          values.pms_ready_for_showing_at_override,
        pms_overrides: values.pms_overrides
      });
    },
    onSuccess: () => {
      toast.success("Rental info updated");
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update rental info: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => {
      updateProperty();
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  if (type.isPMS && pmsId)
    return (
      <PMSRentalInfoForm
        unitPmsId={pmsId}
        handleChange={handleChange as unknown as HandleChange}
        values={values}
        register={register as unknown as Register}
        footer={
          <SaveCancelButtons
            onSave={(e) => handleSubmit(e)}
            onCancel={onClose}
            saveLoading={isPending}
          />
        }
      />
    );

  return (
    <RentalInfoForm
      showWaitlistToggle
      showReadyForShowing
      canChangeAvailablebyDate={permissions.canChangeAvailablebyDate}
      canChangeRentAmount={permissions.canChangeRentAmount}
      register={register as unknown as Register}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

interface RentalInfoSectionProps {
  propertyId?: number;
  unit: Unit;
}

export const RentalInfoSection = ({
  unit,
  propertyId
}: RentalInfoSectionProps) => {
  const queryClient = useQueryClient();
  const { pms, type } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);

  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  if (isEditing)
    return (
      <EditRentalInfoForm
        onSuccess={() => {
          const queryKey = propertyId
            ? ["properties", String(propertyId)]
            : ["unit", String(unit.property.id), String(unit.id)];

          queryClient.invalidateQueries({
            queryKey
          });
        }}
        onClose={() => setIsEditing(false)}
        unitId={unit.id}
        pmsId={unit.pms_id}
        initialValues={{
          monthly_rent_amount:
            unit.monthly_rent_amount && "cents" in unit.monthly_rent_amount
              ? unit.monthly_rent_amount.cents / 100
              : undefined,
          home_rent_amount:
            unit.home_rent_amount && "cents" in unit.home_rent_amount
              ? unit.home_rent_amount.cents / 100
              : undefined,
          site_rent_amount:
            unit.site_rent_amount && "cents" in unit.site_rent_amount
              ? unit.site_rent_amount.cents / 100
              : undefined,
          sale_price:
            unit.sale_price && "cents" in unit.sale_price
              ? unit.sale_price.cents / 100
              : undefined,
          purchase_price:
            unit.purchase_price && "cents" in unit.purchase_price
              ? unit.purchase_price.cents / 100
              : undefined,
          application_fee_cents: unit.application_fee_cents ?? null,
          security_deposit_cents: unit.security_deposit_cents ?? null,
          application_fee_overridden: unit.application_fee_overridden ?? false,
          security_deposit_overridden:
            unit.security_deposit_overridden ?? false,
          occupied: unit.occupied,
          available_at: unit.available_at,
          ready_for_showing_at: unit.ready_for_showing_at,
          waitlist: unit.waitlist,
          pms_monthly_rent_amount_override:
            unit.pms_monthly_rent_amount_override,
          pms_monthly_rent_amount:
            unit.pms_monthly_rent_amount &&
            "cents" in unit.pms_monthly_rent_amount
              ? unit.pms_monthly_rent_amount.cents / 100
              : undefined,
          pms_home_rent_amount_override: unit.pms_home_rent_amount_override,
          pms_home_rent_amount:
            unit.pms_home_rent_amount && "cents" in unit.pms_home_rent_amount
              ? unit.pms_home_rent_amount.cents / 100
              : undefined,
          pms_site_rent_amount_override: unit.pms_site_rent_amount_override,
          pms_site_rent_amount:
            unit.pms_site_rent_amount && "cents" in unit.pms_site_rent_amount
              ? unit.pms_site_rent_amount.cents / 100
              : undefined,
          pms_occupied_override: unit.pms_occupied_override,
          pms_occupied: unit.pms_occupied,
          pms_available_at_override: unit.pms_available_at_override,
          pms_available_at: unit.pms_available_at,
          pms_ready_for_showing_at_override:
            unit.pms_ready_for_showing_at_override,
          pms_ready_for_showing_at: unit.pms_ready_for_showing_at,
          pms_values: unit.pms_values,
          pms_overrides: unit.pms_overrides
        }}
      />
    );

  return (
    <SectionCard
      title="Rental information"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        {homeSiteRentEnabled ? (
          <>
            <Row
              title="Home rent"
              value={
                unit.home_rent_amount
                  ? formatToCurrency(unit.home_rent_amount)
                  : ""
              }
            />
            <Row
              title="Site rent"
              value={
                unit.site_rent_amount
                  ? formatToCurrency(unit.site_rent_amount)
                  : ""
              }
            />
          </>
        ) : (
          <Row
            title="Monthly rent"
            value={
              unit.monthly_rent_amount
                ? formatToCurrency(unit.monthly_rent_amount)
                : ""
            }
          />
        )}
        <Row
          title="Sale price"
          value={unit.sale_price ? formatToCurrency(unit.sale_price) : ""}
        />
        <Row
          title="Purchase price"
          value={
            unit.purchase_price ? formatToCurrency(unit.purchase_price) : ""
          }
        />
        <Show if={pms.isAppfolio || pms.isRentManager}>
          <Row
            title="Security deposit"
            value={
              unit.security_deposit_cents != null
                ? formatToCurrency({
                    cents: unit.security_deposit_cents,
                    currency: "USD"
                  })
                : ""
            }
          />
        </Show>
        <Show if={pms.isAppfolio}>
          <Row
            title="Application fee"
            value={
              unit.application_fee_cents != null
                ? formatToCurrency({
                    cents: unit.application_fee_cents,
                    currency: "USD"
                  })
                : ""
            }
          />
        </Show>
        <Row
          title="Current status"
          value={
            <Badge
              variant={
                unit.occupied ? BadgeVariant.PRIMARY : BadgeVariant.SUCCESS
              }
              size={BadgeSize.SM}
            >
              {unit.occupied ? "Occupied" : "Available"}
            </Badge>
          }
        />
        <Row
          title="Available at"
          value={
            unit.available_at
              ? moment(unit.available_at).utc().format("MMM D, YYYY")
              : ""
          }
        />
        <Row
          title="Ready for showing"
          value={
            unit.ready_for_showing_at
              ? moment(unit.ready_for_showing_at).utc().format("MMM D, YYYY")
              : ""
          }
        />
        {/* Always shown, on or off, so the card states the unit's waitlist status
            rather than leaving its absence to be read as either. */}
        <Row
          title="Waitlist"
          value={
            unit.waitlist ? (
              <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                Active now
              </Badge>
            ) : (
              <span className="text-content-300">
                Will activate when the unit is occupied
              </span>
            )
          }
        />
      </div>
    </SectionCard>
  );
};
